import { useEffect, useMemo, useState } from 'react';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  Icon,
  Page,
  Text,
  TextField,
} from '@shopify/polaris';
import {
  ClipboardCheckIcon,
  ClipboardIcon,
  HideIcon,
  ViewIcon,
} from '@shopify/polaris-icons';
import { ConfirmApplyModal } from '../components/ConfirmApplyModal';
import { MenuSelect } from '../components/MenuSelect';
import { ResetDefaultsModal } from '../components/ResetDefaultsModal';
import { SettingsFieldRow, SettingsSection } from '../components/SettingsLayout';
import { StatusBadge } from '../components/StatusBadge';
import { presentDependencyMessage, presentSettingsFieldLabel, presentSettingsValue } from '../lib/settingsPresentation';
import { presentCurrentStateBadgeLabel, presentCurrentStateValue } from '../lib/adminUiState';

const TARGET_META: Record<string, { title: string; subtitle: string; techLabel: string }> = {
  'pay-web': {
    title: 'Оплати і checkout',
    subtitle: 'LiqPay і custom checkout. Нова Пошта винесена в окремий розділ налаштувань.',
    techLabel: 'pay-web',
  },
  'onec-worker': {
    title: 'Каталог, замовлення і 1С',
    subtitle: 'Оновлення товарів, перевірка чернеток і передача замовлень у 1С.',
    techLabel: 'onec-worker',
  },
};

const GROUP_META: Record<string, { title: string; subtitle: string }> = {
  payment: {
    title: 'Оплати',
    subtitle: 'Режим LiqPay і ключі, з якими покупець проходить оплату.',
  },
  nova_poshta: {
    title: 'Нова Пошта і ТТН',
    subtitle: 'API ключ, відправник, адреса відправлення і правила створення експрес-накладних.',
  },
  catalog: {
    title: 'Каталог з 1С',
    subtitle: 'Як сайт отримує товари з 1С, коли додає новинки і як прибирає неактуальне.',
  },
  draft_publish: {
    title: 'Чернетки та фото',
    subtitle: 'Що стається з товарами без фото і коли вони автоматично виходять на сайт.',
  },
  orders: {
    title: 'Замовлення в 1С',
    subtitle: 'Коли оплачені замовлення автоматично потрапляють у 1С.',
  },
  safety: {
    title: 'Безпечний режим',
    subtitle: 'Запобіжники для перевірок без реальних змін на сайті.',
  },
};

const PRESET_META: Record<string, { label: string; helpText: string }> = {
  live_production: {
    label: 'Бойовий режим',
    helpText: 'Реальні оплати, автоматичне оновлення каталогу і передача замовлень працюють у бойовому режимі.',
  },
  payment_sandbox: {
    label: 'Тестові оплати',
    helpText: 'Перемикає тільки LiqPay у тестовий режим. Каталог і 1С не чіпає.',
  },
  catalog_paused: {
    label: 'Пауза каталогу',
    helpText: 'Зупиняє автоматичну синхронізацію каталогу, не вимикаючи оплату.',
  },
  emergency_stop: {
    label: 'Аварійна зупинка',
    helpText: 'Зупиняє автоматичні цикли каталогу, чернеток і замовлень.',
  },
};

const HIDDEN_GROUPS_BY_TARGET: Record<string, Set<string>> = {
  'pay-web': new Set(['nova_poshta']),
};

const OPTION_META: Record<string, Record<string, { label: string; helpText?: string }>> = {
  LIQPAY_MODE: {
    sandbox: { label: 'Тестовий режим', helpText: 'Оплати проходять як тестові, реального списання немає.' },
    live: { label: 'Бойовий режим', helpText: 'Реальні оплати через бойові ключі LiqPay.' },
  },
  ONEC_SYNC_MODE: {
    safe_live_rollout: {
      label: 'Обережне оновлення каталогу',
      helpText: 'Обмежений режим для контрольного запуску без різких змін у видимості товарів.',
    },
    managed_scope_live: {
      label: 'Керований каталог з 1С',
      helpText: 'Основний робочий режим: 1С є головним джерелом для керованих товарів.',
    },
    full_catalog_dev: {
      label: 'Тестова повна синхронізація',
      helpText: 'Режим для технічних перевірок. Не використовувати як звичайний бойовий режим.',
    },
  },
};

type SettingHelpCopy = {
  summary: string;
  effect: string;
  caution?: string;
};

const LIQPAY_PROFILES = [
  {
    key: 'sandbox',
    title: 'Тестові ключі',
    description: 'Тестові платежі без реального списання.',
    publicKey: 'LIQPAY_SANDBOX_PUBLIC_KEY',
    privateKey: 'LIQPAY_SANDBOX_PRIVATE_KEY',
  },
  {
    key: 'live',
    title: 'Бойові ключі',
    description: 'Бойові платежі реальними картками.',
    publicKey: 'LIQPAY_LIVE_PUBLIC_KEY',
    privateKey: 'LIQPAY_LIVE_PRIVATE_KEY',
  },
];

const SETTING_HELP: Record<string, SettingHelpCopy> = {
  LIQPAY_MODE: {
    summary: 'Визначає, яку пару ключів використовувати: тестову або бойову.',
    effect: 'Тестовий режим не списує реальні гроші. Бойовий режим приймає реальні платежі й повертає клієнта назад у магазин.',
    caution: 'Бойовий режим має сенс тільки якщо заповнені бойові публічний і приватний ключі.',
  },
  LIQPAY_SANDBOX_PUBLIC_KEY: {
    summary: 'Публічний ключ тестового LiqPay.',
    effect: 'Використовується тільки коли LiqPay у тестовому режимі.',
    caution: 'Без тестового приватного ключа пара неповна.',
  },
  LIQPAY_SANDBOX_PRIVATE_KEY: {
    summary: 'Приватний ключ тестового LiqPay для підпису й callback/status.',
    effect: 'Система зможе створювати тестову оплату і перевіряти її відповідь від LiqPay.',
    caution: 'Секрет. Не повертається до стандартного значення і не показується повністю.',
  },
  LIQPAY_LIVE_PUBLIC_KEY: {
    summary: 'Публічний ключ бойового LiqPay.',
    effect: 'Використовується тільки коли LiqPay у бойовому режимі.',
    caution: 'Без бойового приватного ключа пара неповна.',
  },
  LIQPAY_LIVE_PRIVATE_KEY: {
    summary: 'Приватний ключ бойового LiqPay для реальних платежів.',
    effect: 'Система підписує реальну оплату і перевіряє її відповідь від LiqPay.',
    caution: 'Це бойовий секрет. Міняти тільки разом із відповідним публічним ключем.',
  },
  SCHEDULER_ENABLED: {
    summary: 'Головний перемикач автоматичних оновлень.',
    effect: 'Коли вимкнено, автоматична синхронізація каталогу й фонові цикли не стартують за розкладом.',
    caution: 'Ручні дії можуть залишатися доступними, але бойова автоматизація буде на паузі.',
  },
  ONEC_SYNC_INTERVAL_MINUTES: {
    summary: 'Інтервал повної синхронізації каталогу з 1С.',
    effect: 'Через цей проміжок система забирає актуальні дані з 1С і оновлює керовані товари на сайті.',
    caution: 'Занадто малий інтервал створює зайве навантаження.',
  },
  ONEC_SYNC_RETRY_MINUTES: {
    summary: 'Пауза перед повтором після помилки синхронізації.',
    effect: 'Якщо 1С або Shopify тимчасово недоступні, система повторює спробу пізніше, а не безперервно.',
  },
  ONEC_SYNC_MODE: {
    summary: 'Вибирає модель роботи каталогу.',
    effect: 'Основний режим працює з товарами, якими керує 1С: додає відсутнє, оновлює наявність і прибирає неактуальне.',
    caution: 'Тестові режими повної синхронізації не варто вмикати без технічної причини.',
  },
  ONEC_CREATE_PRODUCTS: {
    summary: 'Дозволяє створювати в Shopify товари, які прийшли з 1С, але ще відсутні на сайті.',
    effect: 'Новий товар без фото створюється як чернетка, щоб не потрапити на вітрину сирим.',
  },
  ONEC_CREATE_VARIANTS: {
    summary: 'Дозволяє створювати нові розміри/варіанти для керованих товарів.',
    effect: 'Якщо 1С віддає новий розмір із ціною та залишком, Shopify отримає цей варіант.',
    caution: 'Розміри без ціни з 1С не беремо до уваги за підтвердженням Романа.',
  },
  ONEC_ENABLE_ARCHIVE_PASS: {
    summary: 'Прибирає з вітрини те, що більше не входить в актуальний перелік товарів з 1С.',
    effect: 'Захищає каталог від старих товарів у магазині, яких уже немає в головному джерелі.',
    caution: 'Сильна дія. Має працювати тільки в основному режимі каталогу з 1С.',
  },
  ONEC_ENABLE_VARIANT_LIFECYCLE_SYNC: {
    summary: 'Керує видимістю розмірів на основі актуальних даних 1С.',
    effect: 'Варіант без актуальної наявності/ціни не повинен бути доступним для продажу.',
  },
  ONEC_SYNC_PRODUCT_STATUS_FROM_VARIANTS: {
    summary: 'Синхронізує статус товару з доступністю його керованих варіантів.',
    effect: 'Якщо всі керовані розміри недоступні, товар може перейти у чернетку.',
    caution: 'Це впливає на видимість товару для покупця.',
  },
  ONEC_ENABLE_DRAFT_PUBLISH_RECHECK: {
    summary: 'Фоново перевіряє чернетки товарів на готовність до публікації.',
    effect: 'Якщо менеджер додав фото і товар має доступні розміри, система може автоматично опублікувати його на сайті.',
  },
  ONEC_DRAFT_PUBLISH_RECHECK_INTERVAL_MINUTES: {
    summary: 'Як часто перевіряти чернетки товарів після створення або оновлення.',
    effect: 'Наприклад, 20 хвилин означає регулярну перевірку товарів, які вже можна опублікувати.',
  },
  ONEC_ENABLE_SYNC_ORDERS: {
    summary: 'Дозволяє передавати оплачені Shopify-замовлення в 1С.',
    effect: 'Після підтвердженої оплати замовлення потрапляє в чергу, а система передає його в 1С.',
    caution: 'Якщо вимкнути, оплати лишаються в Shopify, але 1С автоматично їх не отримає.',
  },
  ONEC_QUEUED_ORDERS_INTERVAL_MINUTES: {
    summary: 'Як часто система перевіряє чергу оплачених замовлень.',
    effect: 'Малий інтервал швидше передає замовлення в 1С без ручного запуску.',
  },
  ONEC_QUEUED_ORDERS_BATCH_SIZE: {
    summary: 'Скільки замовлень система бере за один прохід.',
    effect: 'Обмежує навантаження на 1С, якщо за короткий час прийшло багато оплат.',
  },
  ONEC_DRY_RUN: {
    summary: 'Тестовий запобіжник для сценаріїв без бойових записів у Shopify.',
    effect: 'Коли ввімкнено, система може рахувати й показувати зміни, але не має записувати їх на сайт.',
    caution: 'Для реальної синхронізації має бути вимкнено.',
  },
};

function serializeValue(setting: any, value: any) {
  if (setting.type === 'bool') return value ? 'true' : 'false';
  if (setting.type === 'int') return String(value ?? '');
  return String(value ?? '');
}

function buildInitialDrafts(snapshot: any) {
  const drafts: Record<string, Record<string, any>> = {
    'pay-web': {},
    'onec-worker': {},
  };

  Object.values(snapshot?.targets || {}).forEach((target: any) => {
    (target.settings || []).forEach((setting: any) => {
      drafts[target.target][setting.key] = setting.secret ? '' : setting.currentValue;
    });
  });

  return drafts;
}

function groupSettings(settings: any[]) {
  return settings.reduce((groups: Record<string, any[]>, setting: any) => {
    if (!groups[setting.group]) groups[setting.group] = [];
    groups[setting.group].push(setting);
    return groups;
  }, {});
}

function buildChanges(targetSettings: any[], values: Record<string, any>) {
  const changes: Record<string, any> = {};

  targetSettings.forEach((setting) => {
    const nextValue = values[setting.key];
    if (setting.secret) {
      if (nextValue) changes[setting.key] = nextValue;
      return;
    }

    const currentSerialized = serializeValue(setting, typeof setting.currentValue === 'object' ? '' : setting.currentValue);
    const nextSerialized = serializeValue(setting, nextValue);
    if (currentSerialized !== nextSerialized) {
      changes[setting.key] = nextValue;
    }
  });

  return changes;
}

function hasTargetChanges(targetSettings: any[], values: Record<string, any>) {
  return Object.keys(buildChanges(targetSettings, values)).length > 0;
}

function translatePresetTargets(preset: any) {
  return [
    { target: 'pay-web', changes: preset?.changes?.payWeb || {} },
    { target: 'onec-worker', changes: preset?.changes?.onecWorker || {} },
  ].filter((entry) => Object.keys(entry.changes).length > 0);
}

function targetTitle(target: string) {
  return TARGET_META[target]?.title || target;
}

function targetSubtitle(target: string) {
  return TARGET_META[target]?.subtitle || 'Цей блок керує окремою частиною магазину.';
}

function presetLabel(preset: any) {
  return PRESET_META[preset?.key]?.label || preset?.label || preset?.key || '—';
}

function presetHelpText(preset: any) {
  return PRESET_META[preset?.key]?.helpText || preset?.description || 'Готовий набір змін для швидкого перемикання режиму.';
}

function settingOption(setting: any, option: string) {
  const meta = OPTION_META[setting.key]?.[option];
  return {
    label: meta?.label || option,
    value: option,
    helpText: meta?.helpText,
  };
}

function settingHelp(setting: any): SettingHelpCopy {
  return SETTING_HELP[setting.key] || {
    summary: setting.description || 'Керує окремою частиною роботи магазину.',
    effect: 'Спочатку зміна показується у перевірці, і лише потім її можна застосувати.',
  };
}

function settingDisplayLabel(setting: any) {
  return presentSettingsFieldLabel(setting?.key || setting?.label || '');
}

function presentCredentialFingerprint(configured?: boolean, fingerprint?: string | null) {
  if (!configured) return 'не задано';
  return fingerprint ? `збережено · контрольний код ${fingerprint}` : 'збережено';
}

function presentCatalogIssues(issues: string[]) {
  if (issues.length === 0) return '';
  if (issues.length === 1) return `Що зараз стримує каталог: ${issues[0]}`;
  return `Що зараз стримує каталог: ${issues.join(' ')}`;
}

function allSettingsForTarget(target: any) {
  return Object.values(target.groups || {}).flat() as any[];
}

function visibleSettingsForTarget(target: any) {
  const hiddenGroups = HIDDEN_GROUPS_BY_TARGET[target.target] || new Set();
  return (target.settings || []).filter((setting: any) => !hiddenGroups.has(setting.group));
}

function findSetting(settings: any[], key: string) {
  return settings.find((setting) => setting.key === key);
}

function secretStateKey(target: string, key: string) {
  return `${target}:${key}`;
}

function presentSettingsError(message: string) {
  return presentDependencyMessage(message);
}

async function writeClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

function SettingExplanation({ setting, target }: { setting: any; target: string }) {
  const help = settingHelp(setting);
  const supportLabel = settingDisplayLabel(setting);

  return (
    <details className="admin-setting-explanation">
      <summary>Що робить це налаштування</summary>
      <div className="admin-setting-explanation__content">
        <Text as="p" variant="bodySm"><strong>Навіщо:</strong> {help.summary}</Text>
        <Text as="p" variant="bodySm"><strong>Що зміниться:</strong> {help.effect}</Text>
        {help.caution ? <Text as="p" variant="bodySm" tone="subdued"><strong>Увага:</strong> {help.caution}</Text> : null}
        <div className="admin-setting-explanation__tech">
          <Text as="p" variant="bodySm" tone="subdued">Назва для підтримки: {supportLabel}</Text>
          <Text as="p" variant="bodySm" tone="subdued">Сервіс: {targetTitle(target)}</Text>
        </div>
      </div>
    </details>
  );
}

function SettingSourceNote({ setting }: { setting: any }) {
  if (setting.valueSource !== 'default' || setting.secret) return null;

  return (
    <div className="admin-setting-source-note">
      <Text as="p" variant="bodySm" tone="subdued">
        Це значення зараз не записане окремо. Система використовує свій стандартний робочий варіант.
      </Text>
    </div>
  );
}

function CurrentSettingsState({ snapshot }: { snapshot: any }) {
  const current = snapshot?.modeSummary;
  if (!current) return null;

  const catalogIssues = Array.isArray(current.catalog?.issues) ? current.catalog.issues : [];
  const rows = [
    {
      key: 'payment',
      label: 'Оплати',
      value: presentCurrentStateValue('payment', current.payment),
      status: current.payment?.status,
      statusLabel: presentCurrentStateBadgeLabel('payment', current.payment),
    },
    {
      key: 'catalog',
      label: 'Каталог',
      value: presentCurrentStateValue('catalog', current.catalog),
      status: current.catalog?.status,
      statusLabel: presentCurrentStateBadgeLabel('catalog', current.catalog, { issuesCount: catalogIssues.length }),
      help: presentCatalogIssues(catalogIssues),
    },
    {
      key: 'orders',
      label: 'Замовлення',
      value: presentCurrentStateValue('orders', current.orders),
      status: current.orders?.status,
      statusLabel: presentCurrentStateBadgeLabel('orders', current.orders),
    },
    {
      key: 'safety',
      label: 'Безпека',
      value: presentCurrentStateValue('safety', current.safety),
      status: current.safety?.status,
      statusLabel: presentCurrentStateBadgeLabel('safety', current.safety),
    },
    {
      key: 'drafts',
      label: 'Чернетки',
      value: presentCurrentStateValue('drafts', current.drafts),
      status: current.drafts?.status,
      statusLabel: presentCurrentStateBadgeLabel('drafts', current.drafts),
    },
  ];

  return (
    <Card>
      <div className="admin-current-state">
        <div className="admin-section-head">
          <div>
            <Text as="h2" variant="headingMd">Поточний стан</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Тут показано, як магазин працює просто зараз: що вже в бойовому режимі, а де ще є обмеження.
            </Text>
          </div>
        </div>
        <div className="admin-current-state-list">
          {rows.map((row) => (
            <div key={row.label} className="admin-current-state-list__row">
              <span>{row.label}</span>
              <div className="admin-current-state-list__value">
                <strong>{row.value}</strong>
                {row.help ? <small>{row.help}</small> : null}
              </div>
              <div className="admin-current-state-list__badge">
                <StatusBadge status={row.status as string} label={row.statusLabel} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function CredentialProfileSummary({ snapshot }: { snapshot: any }) {
  const profiles = snapshot?.modeSummary?.payment?.profiles;
  if (!profiles) return null;

  const rows = [
    ['Тестові ключі', profiles.sandbox],
    ['Бойові ключі', profiles.live],
    ['Резервний профіль', profiles.legacy],
  ];

  return (
    <div className="admin-credential-summary">
      {rows.map(([label, profile]: any) => (
        <div key={label} className="admin-credential-summary__item">
          <span>{label}</span>
          <strong>{profile.configured ? 'Пара ключів є' : 'Пара неповна/відсутня'}</strong>
          <small>
            публічний ключ: {presentCredentialFingerprint(profile.publicKeyConfigured, profile.publicKeyFingerprint)} · приватний ключ: {presentCredentialFingerprint(profile.privateKeyConfigured, profile.privateKeyFingerprint)}
          </small>
        </div>
      ))}
    </div>
  );
}

function TargetContextNote({ target, snapshot }: { target: string; snapshot: any }) {
  if (target === 'pay-web') {
    const active = snapshot?.modeSummary?.payment?.profiles?.active;
    const activeModeLabel = active?.mode === 'sandbox'
      ? 'Тестові ключі'
      : active?.mode === 'live'
        ? 'Бойові ключі'
        : active?.mode === 'legacy'
          ? 'Резервний профіль'
          : '—';
    return (
      <div className="admin-settings-context-note">
        <Text as="p" variant="bodySm">
          <strong>Як працюють ключі:</strong> режим LiqPay вище визначає, які ключі використовує оплата на сайті: тестові або бойові.
        </Text>
        <Text as="p" variant="bodySm" tone="subdued">
          Зараз активний профіль: {activeModeLabel}. Стан: {active?.configured ? 'усі потрібні ключі є' : 'пари ключів бракує'}.
        </Text>
        <CredentialProfileSummary snapshot={snapshot} />
      </div>
    );
  }

  if (target === 'onec-worker') {
    return (
      <div className="admin-settings-context-note">
        <Text as="p" variant="bodySm">
          <strong>Як зараз працює автоматизація:</strong> один сервіс оновлює каталог з 1С, перевіряє чернетки та передає оплачені замовлення в 1С.
        </Text>
      </div>
    );
  }

  return null;
}

function SettingControl({
  target,
  setting,
  draftValue,
  onChange,
}: {
  target: string;
  setting: any;
  draftValue: any;
  onChange: (value: any) => void;
}) {
  const label = settingDisplayLabel(setting);

  return (
    <SettingsFieldRow>
      {setting.secret ? (
        <TextField
          autoComplete="off"
          label={label}
          value={draftValue || ''}
          type="password"
          placeholder={setting.currentValue?.configured ? 'Введіть нове значення, якщо треба замінити' : 'Не налаштовано'}
          onChange={onChange}
          helpText={setting.description}
        />
      ) : setting.type === 'bool' ? (
        <Checkbox
          label={label}
          checked={Boolean(draftValue)}
          helpText={setting.description}
          onChange={(checked) => onChange(checked)}
        />
      ) : setting.type === 'enum' ? (
        <MenuSelect
          label={label}
          options={(setting.options || []).map((option: string) => settingOption(setting, option))}
          value={String(draftValue ?? '')}
          onChange={onChange}
        />
      ) : (
        <TextField
          autoComplete="off"
          label={label}
          type={setting.type === 'int' ? 'number' : 'text'}
          value={String(draftValue ?? '')}
          onChange={(nextValue) => onChange(setting.type === 'int' ? Number(nextValue) : nextValue)}
          helpText={setting.description}
        />
      )}
      <SettingSourceNote setting={setting} />
      <SettingExplanation setting={setting} target={target} />
    </SettingsFieldRow>
  );
}

function LiqPayCredentialField({
  target,
  setting,
  kind,
  draftValue,
  revealedValue,
  visible,
  actionFeedback,
  saving,
  onChange,
  onReveal,
  onToggleVisible,
  onCopy,
  onSave,
  onRevealFeedback,
}: {
  target: string;
  setting: any;
  kind: 'public' | 'private';
  draftValue: string;
  revealedValue?: string;
  visible?: boolean;
  actionFeedback?: 'copy' | 'view';
  saving?: boolean;
  onChange: (value: string) => void;
  onReveal: (target: string, key: string) => Promise<string>;
  onToggleVisible: (key: string, visible: boolean) => void;
  onCopy: (target: string, setting: any, draftValue: string) => Promise<void>;
  onSave: (target: string, setting: any, draftValue: string) => Promise<void>;
  onRevealFeedback: () => void;
}) {
  const configured = Boolean(setting?.currentValue?.configured);
  const fingerprint = setting?.currentValue?.fingerprint || '—';
  const storedSecretMask = '••••••••••••••••';
  const showsStoredSecretMask = configured && !draftValue && !revealedValue && !visible;
  const value = showsStoredSecretMask ? storedSecretMask : draftValue || revealedValue || '';
  const hasPendingChange = Boolean(String(draftValue || '').trim());
  const canReveal = Boolean(configured || draftValue || revealedValue);
  const canCopy = Boolean(draftValue || revealedValue || configured);
  const label = kind === 'public' ? 'Публічний ключ' : 'Приватний ключ';
  const currentState = configured ? `Ключ збережено${fingerprint !== '—' ? ` · контрольний код ${fingerprint}` : ''}` : 'Ще не додано';

  const handleChange = (nextValue: string) => {
    if (!showsStoredSecretMask) {
      onChange(nextValue);
      return;
    }

    onChange(nextValue.replace(storedSecretMask, '').trimStart());
  };

  const handleReveal = async () => {
    if (!revealedValue && !draftValue) {
      await onReveal(target, setting.key);
    }
    onToggleVisible(setting.key, !visible);
    onRevealFeedback();
  };

  return (
    <div className="admin-liqpay-key">
      <div className="admin-liqpay-key__meta">
        <Text as="p" variant="bodyMd">{label}</Text>
        <Text as="p" variant="bodySm" tone="subdued">{currentState}</Text>
      </div>
      <div className="admin-liqpay-key__control-row">
        <div className="admin-liqpay-key__input">
          <TextField
            autoComplete="off"
            label={label}
            labelHidden
            type={visible ? 'text' : 'password'}
            value={value}
            placeholder={configured ? 'Ключ збережений. Натисніть “Показати” або введіть новий.' : 'Вставте ключ'}
            onChange={handleChange}
          />
        </div>
        <div className="admin-liqpay-key__actions" aria-label={`Дії з ключем: ${label}`}>
          <button
            type="button"
            className={`admin-icon-button admin-view-button ${actionFeedback === 'view' ? 'is-animated' : ''}`}
            disabled={!canReveal}
            onClick={handleReveal}
            title={visible ? 'Сховати ключ' : 'Показати ключ'}
            aria-label={visible ? 'Сховати ключ' : 'Показати ключ'}
          >
            <Icon source={visible ? HideIcon : ViewIcon} />
          </button>
          <button
            type="button"
            className={`admin-icon-button admin-copy-button ${actionFeedback === 'copy' ? 'is-copied' : ''}`}
            disabled={!canCopy}
            onClick={() => onCopy(target, setting, draftValue || '')}
            title="Скопіювати ключ"
            aria-label="Скопіювати ключ"
          >
            <Icon source={actionFeedback === 'copy' ? ClipboardCheckIcon : ClipboardIcon} />
          </button>
          {hasPendingChange ? (
            <Button
              size="slim"
              variant="primary"
              loading={saving}
              onClick={() => onSave(target, setting, draftValue)}
            >
              Зберегти
            </Button>
          ) : null}
        </div>
      </div>
      <Text as="p" variant="bodySm" tone="subdued">
        Якщо поле лишити порожнім, збережений ключ не зміниться.
      </Text>
    </div>
  );
}

function LiqPayProfileCard({
  target,
  profile,
  settings,
  profilesSnapshot,
  drafts,
  revealedSecrets,
  visibleSecrets,
  secretActionFeedback,
  savingSecrets,
  onDraftChange,
  onReveal,
  onToggleVisible,
  onCopy,
  onSave,
  onSecretActionFeedback,
}: {
  target: string;
  profile: any;
  settings: any[];
  profilesSnapshot: any;
  drafts: Record<string, any>;
  revealedSecrets: Record<string, string>;
  visibleSecrets: Record<string, boolean>;
  secretActionFeedback: Record<string, 'copy' | 'view'>;
  savingSecrets: Record<string, boolean>;
  onDraftChange: (key: string, value: string) => void;
  onReveal: (target: string, key: string) => Promise<string>;
  onToggleVisible: (key: string, visible: boolean) => void;
  onCopy: (target: string, setting: any, draftValue: string) => Promise<void>;
  onSave: (target: string, setting: any, draftValue: string) => Promise<void>;
  onSecretActionFeedback: (stateKey: string, action: 'copy' | 'view') => void;
}) {
  const publicSetting = findSetting(settings, profile.publicKey);
  const privateSetting = findSetting(settings, profile.privateKey);
  const profileState = profilesSnapshot?.[profile.key];
  const configured = Boolean(profileState?.configured);
  const active = profilesSnapshot?.active?.mode === profile.key;

  if (!publicSetting || !privateSetting) return null;

  return (
    <div className={`admin-liqpay-profile-card ${active ? 'is-active' : ''}`}>
      <div className="admin-liqpay-profile-card__head">
        <div>
          <Text as="h3" variant="headingSm">Роль ключів: {profile.title}</Text>
          <Text as="p" variant="bodySm" tone="subdued">{profile.description}</Text>
        </div>
        <div className="admin-liqpay-profile-card__status">
          <StatusBadge
            status={active ? 'live' : configured ? 'ok' : 'warning'}
            label={active ? 'Активна' : configured ? 'Пара є' : 'Пара неповна'}
          />
        </div>
      </div>

      <div className="admin-liqpay-profile-card__fields">
        {[{ setting: publicSetting, kind: 'public' as const }, { setting: privateSetting, kind: 'private' as const }].map(({ setting, kind }) => {
          const stateKey = secretStateKey(target, setting.key);

          return (
            <LiqPayCredentialField
              key={setting.key}
              target={target}
              setting={setting}
              kind={kind}
              draftValue={String(drafts[setting.key] || '')}
              revealedValue={revealedSecrets[stateKey]}
              visible={Boolean(visibleSecrets[stateKey])}
              actionFeedback={secretActionFeedback[stateKey]}
              saving={Boolean(savingSecrets[stateKey])}
              onChange={(value) => onDraftChange(setting.key, value)}
              onReveal={onReveal}
              onToggleVisible={(key, nextVisible) => onToggleVisible(secretStateKey(target, key), nextVisible)}
              onCopy={onCopy}
              onSave={onSave}
              onRevealFeedback={() => onSecretActionFeedback(stateKey, 'view')}
            />
          );
        })}
      </div>
    </div>
  );
}

function PaymentSettingsGroup({
  target,
  settings,
  snapshot,
  drafts,
  revealedSecrets,
  visibleSecrets,
  secretActionFeedback,
  savingSecrets,
  onDraftChange,
  onRevealSecret,
  onToggleSecretVisible,
  onCopySecret,
  onSaveSecret,
  onSecretActionFeedback,
}: {
  target: string;
  settings: any[];
  snapshot: any;
  drafts: Record<string, any>;
  revealedSecrets: Record<string, string>;
  visibleSecrets: Record<string, boolean>;
  secretActionFeedback: Record<string, 'copy' | 'view'>;
  savingSecrets: Record<string, boolean>;
  onDraftChange: (key: string, value: any) => void;
  onRevealSecret: (target: string, key: string) => Promise<string>;
  onToggleSecretVisible: (stateKey: string, visible: boolean) => void;
  onCopySecret: (target: string, setting: any, draftValue: string) => Promise<void>;
  onSaveSecret: (target: string, setting: any, draftValue: string) => Promise<void>;
  onSecretActionFeedback: (stateKey: string, action: 'copy' | 'view') => void;
}) {
  const modeSetting = findSetting(settings, 'LIQPAY_MODE');
  const profiles = snapshot?.modeSummary?.payment?.profiles;

  return (
    <div className="admin-liqpay-manager">
      {modeSetting ? (
        <SettingControl
          target={target}
          setting={modeSetting}
          draftValue={drafts[modeSetting.key]}
          onChange={(value) => onDraftChange(modeSetting.key, value)}
        />
      ) : null}

      <div className="admin-liqpay-manager__intro">
        <Text as="p" variant="bodySm">
          Кожна роль має власну пару ключів. Перемикач LiqPay вище лише вибирає, яку пару використовує покупець під час оплати.
        </Text>
      </div>

      <div className="admin-liqpay-profiles">
        {LIQPAY_PROFILES.map((profile) => (
          <LiqPayProfileCard
            key={profile.key}
            target={target}
            profile={profile}
            settings={settings}
            profilesSnapshot={profiles}
            drafts={drafts}
            revealedSecrets={revealedSecrets}
            visibleSecrets={visibleSecrets}
            secretActionFeedback={secretActionFeedback}
            savingSecrets={savingSecrets}
            onDraftChange={onDraftChange}
            onReveal={onRevealSecret}
            onToggleVisible={onToggleSecretVisible}
            onCopy={onCopySecret}
            onSave={onSaveSecret}
            onSecretActionFeedback={onSecretActionFeedback}
          />
        ))}
      </div>
    </div>
  );
}

function directoryOptionLabel(item: any) {
  return item?.label || item?.description || item?.fullName || item?.ref || '—';
}

function NovaDirectoryOption({
  title,
  item,
  onUse,
}: {
  title: string;
  item: any;
  onUse: () => void;
}) {
  if (!item) return null;

  return (
    <div className="admin-nova-directory__option">
      <div>
        <Text as="p" variant="bodyMd">{title}</Text>
        <Text as="p" variant="bodySm" tone="subdued">{directoryOptionLabel(item)}</Text>
        <Text as="p" variant="bodySm" tone="subdued">Ref: {item.ref}</Text>
      </div>
      <Button size="slim" onClick={onUse}>Взяти</Button>
    </div>
  );
}

function NovaPoshtaSettingsGroup({
  target,
  settings,
  drafts,
  api,
  notify,
  onDraftChange,
}: {
  target: string;
  settings: any[];
  drafts: Record<string, any>;
  api: any;
  notify: (toast: any) => void;
  onDraftChange: (key: string, value: any) => void;
}) {
  const [directory, setDirectory] = useState<any>(null);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState('');

  const loadDirectory = async (senderRef = drafts.NOVA_POSHTA_SENDER_REF || '') => {
    setDirectoryLoading(true);
    setDirectoryError('');
    try {
      const result = await api.getNovaPoshtaSenderDirectory(senderRef || undefined);
      setDirectory(result);
      notify({
        tone: 'success',
        title: 'Нова Пошта відповіла',
        message: 'Довідник відправника завантажено.',
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося завантажити довідник Нової Пошти';
      const presentedMessage = presentSettingsError(message);
      setDirectoryError(presentedMessage);
      notify({
        tone: 'critical',
        title: 'Нова Пошта недоступна',
        message: presentedMessage,
      });
    } finally {
      setDirectoryLoading(false);
    }
  };

  const applyDirectoryValues = ({
    sender = directory?.selectedSender,
    contact = directory?.contacts?.[0],
    address = directory?.addresses?.[0],
  }: any = {}) => {
    if (sender?.ref) onDraftChange('NOVA_POSHTA_SENDER_REF', sender.ref);
    if (sender?.cityRef) onDraftChange('NOVA_POSHTA_SENDER_CITY_REF', sender.cityRef);
    if (contact?.ref) onDraftChange('NOVA_POSHTA_SENDER_CONTACT_REF', contact.ref);
    if (contact?.phone) onDraftChange('NOVA_POSHTA_SENDER_PHONE', contact.phone);
    if (address?.ref) onDraftChange('NOVA_POSHTA_SENDER_ADDRESS_REF', address.ref);

    notify({
      tone: 'success',
      title: 'Дані підставлено',
      message: 'Перевірте поля нижче і натисніть “Показати що зміниться”.',
    });
  };

  const selectedSender = directory?.selectedSender;
  const firstContact = directory?.contacts?.[0];
  const firstAddress = directory?.addresses?.[0];
  const hasDirectory = Boolean(directory?.status === 'ok');

  return (
    <div className="admin-nova-settings">
      <div className="admin-nova-directory">
        <div className="admin-nova-directory__head">
          <div>
            <Text as="h4" variant="headingSm">Дані відправника з кабінету Нової Пошти</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Після збереження API ключа тут можна підтягнути відправників, контакти й адреси, які потрібні для створення ТТН.
            </Text>
          </div>
          <Button loading={directoryLoading} onClick={() => loadDirectory()}>
            Підтягнути з API
          </Button>
        </div>

        {directoryError ? (
          <Banner tone="critical" title="Не вдалося завантажити Нову Пошту">
            <p>{directoryError}</p>
          </Banner>
        ) : null}

        {hasDirectory ? (
          <div className="admin-nova-directory__body">
            <div className="admin-nova-directory__summary">
              <StatusBadge status="ok" label="Ключ працює" />
              <Text as="p" variant="bodySm" tone="subdued">
                Відправників: {directory.senders?.length || 0} · контактів: {directory.contacts?.length || 0} · адрес: {directory.addresses?.length || 0}
              </Text>
              <Button
                size="slim"
                variant="primary"
                disabled={!selectedSender}
                onClick={() => applyDirectoryValues()}
              >
                Заповнити першими даними
              </Button>
            </div>

            <div className="admin-nova-directory__options">
              <NovaDirectoryOption
                title="Відправник"
                item={selectedSender}
                onUse={() => applyDirectoryValues({ sender: selectedSender })}
              />
              <NovaDirectoryOption
                title="Контакт"
                item={firstContact}
                onUse={() => applyDirectoryValues({ contact: firstContact })}
              />
              <NovaDirectoryOption
                title="Адреса відправлення"
                item={firstAddress}
                onUse={() => applyDirectoryValues({ address: firstAddress })}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="admin-settings-field-list">
        {settings.map((setting) => (
          <SettingControl
            key={setting.key}
            target={target}
            setting={setting}
            draftValue={drafts[setting.key]}
            onChange={(value) => onDraftChange(setting.key, value)}
          />
        ))}
      </div>
    </div>
  );
}

export function SettingsPage({ api, onRefreshOverview, notify, readOnly = false }: any) {
  const [snapshot, setSnapshot] = useState<any>(null);
  const [drafts, setDrafts] = useState<Record<string, Record<string, any>>>({ 'pay-web': {}, 'onec-worker': {} });
  const [error, setError] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [pendingPreviews, setPendingPreviews] = useState<any[]>([]);
  const [pendingApply, setPendingApply] = useState<any[]>([]);
  const [applyBusy, setApplyBusy] = useState(false);
  const [resetTarget, setResetTarget] = useState<any>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [revealedSecrets, setRevealedSecrets] = useState<Record<string, string>>({});
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});
  const [secretActionFeedback, setSecretActionFeedback] = useState<Record<string, 'copy' | 'view'>>({});
  const [savingSecrets, setSavingSecrets] = useState<Record<string, boolean>>({});

  const notifyLocal = (toast: any) => {
    if (notify) notify(toast);
  };

  const refresh = async () => {
    try {
      const nextSnapshot = await api.getSettings();
      setSnapshot(nextSnapshot);
      setDrafts(buildInitialDrafts(nextSnapshot));
      setRevealedSecrets({});
      setVisibleSecrets({});
      setSecretActionFeedback({});
      setSavingSecrets({});
      setError('');
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося прочитати settings';
      const presentedMessage = presentSettingsError(message);
      setError(presentedMessage);
      notifyLocal({ tone: 'critical', title: 'Налаштування недоступні', message: presentedMessage });
    }
  };

  const revealSecret = async (target: string, key: string) => {
    const stateKey = secretStateKey(target, key);
    if (revealedSecrets[stateKey] !== undefined) {
      return revealedSecrets[stateKey];
    }

    try {
      const result = await api.revealSettingSecret(target, key);
      const value = result?.value || '';
      setRevealedSecrets((current) => ({ ...current, [stateKey]: value }));
      return value;
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося показати ключ';
      notifyLocal({ tone: 'critical', title: 'Ключ не відкрився', message: presentSettingsError(message) });
      throw nextError;
    }
  };

  const copySecret = async (target: string, setting: any, draftValue: string) => {
    try {
      const stateKey = secretStateKey(target, setting.key);
      const value = draftValue || revealedSecrets[stateKey] || await revealSecret(target, setting.key);
      if (!value) {
        notifyLocal({ tone: 'info', title: 'Ключ порожній', message: `${settingDisplayLabel(setting)} ще не налаштовано.` });
        return;
      }

      await writeClipboard(value);
      triggerSecretActionFeedback(stateKey, 'copy');
      notifyLocal({ tone: 'success', title: 'Скопійовано', message: `${settingDisplayLabel(setting)} скопійовано.` });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося скопіювати ключ';
      notifyLocal({ tone: 'critical', title: 'Не вдалося скопіювати', message: presentSettingsError(message) });
    }
  };

  const triggerSecretActionFeedback = (stateKey: string, action: 'copy' | 'view') => {
    setSecretActionFeedback((current) => ({ ...current, [stateKey]: action }));
    window.setTimeout(() => {
      setSecretActionFeedback((current) => {
        const next = { ...current };
        delete next[stateKey];
        return next;
      });
    }, 450);
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDraftChange = (target: string, key: string, value: any) => {
    setDrafts((current) => ({
      ...current,
      [target]: {
        ...current[target],
        [key]: value,
      },
    }));
  };

  const saveSecret = async (target: string, setting: any, draftValue: string) => {
    const value = String(draftValue || '').trim();
    const stateKey = secretStateKey(target, setting.key);
    if (!value) {
      notifyLocal({ tone: 'info', title: 'Немає нового ключа', message: 'Порожнє поле не змінює збережений ключ.' });
      return;
    }

    setSavingSecrets((current) => ({ ...current, [stateKey]: true }));
    try {
      setError('');
      const changes = { [setting.key]: value };
      const preview = await api.previewSettings(target, changes);
      if (preview?.errors?.length) {
        throw new Error(preview.errors.join(' '));
      }

      const result = await api.applySettings(target, changes, preview.revision);
      if (result?.errors?.length) {
        throw new Error(result.errors.join(' '));
      }

      const degraded = result?.status === 'degraded';
      notifyLocal({
        tone: degraded ? 'warning' : 'success',
        title: degraded ? 'Ключ записано, але потрібна технічна перевірка' : 'Ключ збережено',
        message: degraded
          ? 'Файл оновився, але runtime-перевірка повернула попередження.'
          : `${settingDisplayLabel(setting)} оновлено.`,
      });
      await refresh();
      await onRefreshOverview();
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося зберегти ключ';
      const presentedMessage = presentSettingsError(message);
      setError(presentedMessage);
      notifyLocal({ tone: 'critical', title: 'Ключ не збережено', message: presentedMessage });
    } finally {
      setSavingSecrets((current) => {
        const next = { ...current };
        delete next[stateKey];
        return next;
      });
    }
  };

  const openConfirm = ({ title, previews, apply }: { title: string; previews: any[]; apply: any[] }) => {
    setPendingPreviews(previews);
    setPendingApply(apply);
    setModalTitle(title);
    setModalOpen(true);
  };

  const previewTarget = async (target: string) => {
    try {
      setError('');
      const targetSnapshot = (Object.values(snapshot.targets || {}) as any[]).find((entry: any) => entry.target === target);
      const changes = buildChanges(targetSnapshot?.settings || [], drafts[target] || {});
      if (Object.keys(changes).length === 0) {
        notifyLocal({ tone: 'info', title: 'Немає змін', message: 'Цей блок уже відповідає поточному стану.' });
        return;
      }

      const preview = await api.previewSettings(target, changes);
      openConfirm({
        title: `Перевірте зміни перед застосуванням: ${targetTitle(target)}`,
        previews: [preview],
        apply: [{ action: 'settings', target, changes, revision: preview.revision }],
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося підготувати перевірку змін';
      const presentedMessage = presentSettingsError(message);
      setError(presentedMessage);
      notifyLocal({ tone: 'critical', title: 'Не вдалося перевірити зміни', message: presentedMessage });
    }
  };

  const previewPreset = async () => {
    try {
      setError('');
      const preset = (snapshot?.presets || []).find((entry: any) => entry.key === selectedPreset);
      if (!preset) {
        notifyLocal({ tone: 'info', title: 'Оберіть сценарій', message: 'Спочатку оберіть сценарій. До підтвердження жодні налаштування не зміняться.' });
        return;
      }

      const targets = translatePresetTargets(preset);
      const previews = [];
      for (const entry of targets) {
        previews.push(await api.previewSettings(entry.target, entry.changes));
      }

      openConfirm({
        title: `Перевірте сценарій перед застосуванням: ${presetLabel(preset)}`,
        previews,
        apply: previews.map((preview, index) => ({
          action: 'settings',
          target: targets[index].target,
          changes: targets[index].changes,
          revision: preview.revision,
        })),
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося перевірити сценарій';
      const presentedMessage = presentSettingsError(message);
      setError(presentedMessage);
      notifyLocal({ tone: 'critical', title: 'Не вдалося перевірити сценарій', message: presentedMessage });
    }
  };

  const previewMaterializeDefaults = async (target: string) => {
    try {
      const preview = await api.previewMaterializeDefaults(target);
      if (!preview.diff?.length) {
        notifyLocal({ tone: 'info', title: 'Стандартні значення вже на місці', message: `${targetTitle(target)} уже має всі відкриті стандартні значення.` });
        return;
      }

      openConfirm({
        title: `Перевірте стандартні значення перед записом: ${targetTitle(target)}`,
        previews: [preview],
        apply: [{ action: 'materialize', target, revision: preview.revision }],
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося підготувати стандартні значення';
      notifyLocal({ tone: 'critical', title: 'Не вдалося перевірити стандартні значення', message: presentSettingsError(message) });
    }
  };

  const previewResetDefaults = async (keys: string[]) => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      const preview = await api.previewResetDefaults(resetTarget.target, keys);
      setResetTarget(null);
      if (!preview.diff?.length) {
        notifyLocal({ tone: 'info', title: 'Немає що повертати', message: 'Обрані налаштування вже відповідають стандартному значенню.' });
        return;
      }

      openConfirm({
        title: `Перевірте повернення до стандарту: ${targetTitle(resetTarget.target)}`,
        previews: [preview],
        apply: [{ action: 'reset', target: resetTarget.target, keys, revision: preview.revision }],
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося підготувати повернення до стандарту';
      notifyLocal({ tone: 'critical', title: 'Не вдалося перевірити повернення до стандарту', message: presentSettingsError(message) });
    } finally {
      setResetBusy(false);
    }
  };

  const applyPendingChanges = async () => {
    setApplyBusy(true);
    try {
      for (const entry of pendingApply) {
        if (entry.action === 'materialize') {
          await api.applyMaterializeDefaults(entry.target, entry.revision);
        } else if (entry.action === 'reset') {
          await api.applyResetDefaults(entry.target, entry.keys, entry.revision);
        } else {
          await api.applySettings(entry.target, entry.changes, entry.revision);
        }
      }
      setModalOpen(false);
      notifyLocal({ tone: 'success', title: 'Налаштування оновлено', message: 'Зміни застосовано і сервіс перезапущено.' });
      setSelectedPreset('');
      await refresh();
      await onRefreshOverview();
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Не вдалося застосувати зміни';
      const presentedMessage = presentSettingsError(message);
      setError(presentedMessage);
      notifyLocal({ tone: 'critical', title: 'Зміни не застосовано', message: presentedMessage });
    } finally {
      setApplyBusy(false);
    }
  };

  const groupedTargets = useMemo(() => {
    return (Object.values(snapshot?.targets || {}) as any[]).map((target: any) => ({
      ...target,
      settings: visibleSettingsForTarget(target),
      groups: groupSettings(visibleSettingsForTarget(target)),
    }));
  }, [snapshot]);

  return (
    <Page title="Налаштування" subtitle="Керування оплатами, каталогом і замовленнями без ручного втручання у файли налаштувань">
      <div className="admin-stack">
        {error ? <Banner tone="critical" title="Налаштування недоступні"><p>{error}</p></Banner> : null}

        <Card>
          <div className="admin-settings-intro">
            <div>
              <Text as="h2" variant="headingMd">Як застосовуються зміни</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Спочатку адмінка показує, що зміниться, які наслідки це матиме для магазину і чи є ризики. Лише після підтвердження вона застосовує зміни до сервісу.
              </Text>
            </div>
            <div className="admin-settings-intro__steps">
              <span>1. Обрати</span>
              <span>2. Перевірити</span>
              <span>3. Підтвердити</span>
            </div>
          </div>
        </Card>

        <CurrentSettingsState snapshot={snapshot} />

        {readOnly ? (
          <Banner tone="info" title="Режим перегляду">
            <p>У цьому режимі можна дивитися поточний стан, але не можна запускати перевірку змін чи застосовувати їх.</p>
          </Banner>
        ) : null}

        <fieldset className="admin-fieldset-reset" disabled={readOnly}>
          <div className="admin-stack">
            <Card>
              <div className="admin-settings-preset">
                <div className="admin-section-head">
                  <div>
                    <Text as="h2" variant="headingMd">Швидкі сценарії</Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Це готові набори для типових ситуацій. Вони не змінюють поточний режим, доки ви не перевірите наслідки і не підтвердите дію.
                    </Text>
                  </div>
                  <Button variant="primary" onClick={previewPreset}>Показати наслідки сценарію</Button>
                </div>
                <div className="admin-settings-preset__body">
                  <MenuSelect
                    label="Сценарій для застосування"
                    value={selectedPreset}
                    onChange={setSelectedPreset}
                    options={[
                      { label: 'Оберіть сценарій', value: '', helpText: 'Нічого не змінюється, доки ви не перевірите наслідки і не підтвердите дію.' },
                      ...((snapshot?.presets || []).map((preset: any) => ({
                        label: presetLabel(preset),
                        value: preset.key,
                        helpText: presetHelpText(preset),
                      }))),
                    ]}
                  />
                  <div className="admin-settings-preset__note">
                    <Text as="p" variant="bodySm" tone="subdued">
                      Сценарій лише збирає пакет змін. Якщо потрібен точковий контроль, змінюйте окремі блоки нижче.
                    </Text>
                  </div>
                </div>
              </div>
            </Card>

            {groupedTargets.map((target: any) => {
              const targetHasChanges = hasTargetChanges(target.settings || [], drafts[target.target] || {});

              return (
                <SettingsSection
                  key={target.target}
                  title={targetTitle(target.target)}
                  description={targetSubtitle(target.target)}
                  action={(
                    <div className="admin-settings-section__actions">
                      <Button onClick={() => previewMaterializeDefaults(target.target)}>Дописати стандартні значення</Button>
                      <Button onClick={() => setResetTarget(target)}>Повернути вибране до стандарту</Button>
                      <Button variant="primary" disabled={!targetHasChanges} onClick={() => previewTarget(target.target)}>Показати що зміниться</Button>
                    </div>
                  )}
                >
                  <div className="admin-stack admin-settings-target">
                    <details className="admin-advanced-details admin-advanced-details--target">
                      <summary>Службові дані для підтримки</summary>
                      <Text as="p" variant="bodySm" tone="subdued">Цей блок має окремий набір налаштувань і перезапускається окремо.</Text>
                      <Text as="p" variant="bodySm" tone="subdued">Сервіс: {targetTitle(target.target)}</Text>
                    </details>
                    <TargetContextNote target={target.target} snapshot={snapshot} />

                    {Object.entries(target.groups || {}).map(([groupKey, settings]) => (
                      <div key={groupKey} className="admin-settings-group">
                        <div className="admin-settings-group__head">
                          <Text as="h3" variant="headingSm">{GROUP_META[groupKey]?.title || groupKey}</Text>
                          <Text as="p" variant="bodySm" tone="subdued">{GROUP_META[groupKey]?.subtitle || 'Налаштування цього блоку.'}</Text>
                        </div>
                        {target.target === 'pay-web' && groupKey === 'payment' ? (
                          <PaymentSettingsGroup
                            target={target.target}
                            settings={settings as any[]}
                            snapshot={snapshot}
                            drafts={drafts[target.target] || {}}
                            revealedSecrets={revealedSecrets}
                            visibleSecrets={visibleSecrets}
                            secretActionFeedback={secretActionFeedback}
                            savingSecrets={savingSecrets}
                            onDraftChange={(key, value) => handleDraftChange(target.target, key, value)}
                            onRevealSecret={revealSecret}
                            onToggleSecretVisible={(stateKey, visible) => setVisibleSecrets((current) => ({ ...current, [stateKey]: visible }))}
                            onCopySecret={copySecret}
                            onSaveSecret={saveSecret}
                            onSecretActionFeedback={triggerSecretActionFeedback}
                          />
                        ) : target.target === 'pay-web' && groupKey === 'nova_poshta' ? (
                          <NovaPoshtaSettingsGroup
                            target={target.target}
                            settings={settings as any[]}
                            drafts={drafts[target.target] || {}}
                            api={api}
                            notify={notifyLocal}
                            onDraftChange={(key, value) => handleDraftChange(target.target, key, value)}
                          />
                        ) : (
                          <div className="admin-settings-field-list">
                            {(settings as any[]).map((setting) => (
                              <SettingControl
                                key={setting.key}
                                target={target.target}
                                setting={setting}
                                draftValue={drafts[target.target]?.[setting.key]}
                                onChange={(value) => handleDraftChange(target.target, setting.key, value)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </SettingsSection>
              );
            })}
          </div>
        </fieldset>

        <ResetDefaultsModal
          open={Boolean(resetTarget)}
          targetTitle={targetTitle(resetTarget?.target || '')}
          settings={resetTarget ? allSettingsForTarget(resetTarget).map((setting) => ({
            ...setting,
            label: settingDisplayLabel(setting),
          })) : []}
          busy={resetBusy}
          onClose={() => setResetTarget(null)}
          onPreview={previewResetDefaults}
        />

        <ConfirmApplyModal
          open={modalOpen}
          title={modalTitle}
          previews={pendingPreviews}
          busy={applyBusy}
          onClose={() => setModalOpen(false)}
          onConfirm={applyPendingChanges}
        />
      </div>
    </Page>
  );
}
